<template>
  <div class="exam-management">
    <!-- 搜索和操作栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="searchForm.title"
          placeholder="搜索考试标题"
          style="width: 300px"
          @keyup.enter="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
        </el-input>
      </div>

      <div class="toolbar-right">
        <el-button type="primary" @click="handleCreate">
          <i class="el-icon-plus"></i>
          新增考试
        </el-button>
        <el-button @click="handleRefresh">
          <i class="el-icon-refresh"></i>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 考试列表 -->
    <el-table :data="examList" stripe style="width: 100%" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" />

      <el-table-column prop="title" label="考试标题" min-width="200">
        <template slot-scope="scope">
          <div class="title-cell">
            <span class="title-text">{{ scope.row.title }}</span>
            <el-tag
              :type="scope.row.enabled ? 'success' : 'danger'"
              size="mini"
              style="margin-left: 8px"
            >
              {{ scope.row.enabled ? "启用" : "禁用" }}
            </el-tag>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="questions" label="题目数量" width="100">
        <template slot-scope="scope">
          <el-tag size="mini" type="info">
            {{ scope.row.questions ? scope.row.questions.length : 0 }}题
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="totalScore" label="总分" width="80">
        <template slot-scope="scope">
          <span class="score-text">{{ scope.row.totalScore || 0 }}分</span>
        </template>
      </el-table-column>

      <el-table-column prop="passScore" label="及格分" width="80">
        <template slot-scope="scope">
          <span class="score-text">{{ scope.row.passScore || 0 }}分</span>
        </template>
      </el-table-column>

      <el-table-column prop="createTime" label="创建时间" width="160">
        <template slot-scope="scope">
          {{ scope.row.createTime | dateFormat("yyyy-MM-dd hh:mm") }}
        </template>
      </el-table-column>

      <el-table-column label="操作" width="250" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" @click="handleView(scope.row)">查看</el-button>
          <el-button size="mini" type="primary" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
      />
    </div>

    <!-- 新增/编辑对话框 -->
    <ExamEditDialog
      :visible.sync="dialogVisible"
      :form-data="currentFormData"
      :mode="dialogMode"
      @confirm="handleDialogConfirm"
    />

    <!-- 查看对话框 -->
    <ExamViewDialog :visible.sync="viewDialogVisible" :exam-data="viewData" />
  </div>
</template>

<script>
import ExamEditDialog from "./ExamEditDialog.vue";
import ExamViewDialog from "./ExamViewDialog.vue";
import { getTrainingExamList, deleteTrainingExam } from "@/api/new_training";
import { ParamUtil } from "@/util";
export default {
  name: "ExamManagement",
  components: {
    ExamEditDialog,
    ExamViewDialog
  },
  data() {
    return {
      examList: [],
      selectedRows: [],

      // 搜索表单
      searchForm: {
        title: ""
      },

      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },

      // 对话框
      dialogVisible: false,
      dialogMode: "create", // create | edit
      currentFormData: {},

      // 查看对话框
      viewDialogVisible: false,
      viewData: {}
    };
  },
  created() {
    this.query();
  },
  methods: {
    // 加载考试列表（后台分页）
    query(page = 1) {
      const param = {
        page: {
          current: page,
          size: this.pagination.pageSize
        },
        param: ParamUtil.filterEmptyData(this.searchForm)
      };
      getTrainingExamList(param)
        .then((res) => {
          const { records = [], current = 1, total = 0 } = res.data || {};
          this.examList = records;
          this.pagination.currentPage = current;
          this.pagination.total = total;
        })
        .catch((error) => {
          this.$message.error("加载考试列表失败");
          console.error(error);
        });
    },

    // 刷新/搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.query(1);
    },

    // 刷新
    handleRefresh() {
      this.query(this.pagination.currentPage);
    },

    // 新增
    handleCreate() {
      this.dialogMode = "create";
      this.currentFormData = {};
      this.dialogVisible = true;
    },

    // 编辑
    handleEdit(row) {
      this.dialogMode = "edit";
      this.currentFormData = { ...row };
      this.dialogVisible = true;
    },

    // 查看
    handleView(row) {
      this.viewData = { ...row };
      this.viewDialogVisible = true;
    },

    // 删除
    handleDelete(row) {
      this.$confirm("确定要删除这个考试吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        try {
          await deleteTrainingExam(row.trainExamId);
          this.$message.success("删除成功");
          this.query(this.pagination.currentPage); // 删除后刷新当前页
        } catch (error) {
          this.$message.error("删除失败");
          console.error(error);
        }
      });
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },

    // 分页变化
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.query(1);
    },

    handleCurrentChange(page) {
      this.query(page);
    },

    // 对话框确认
    handleDialogConfirm() {
      if (this.dialogMode === "create") {
        this.$message.success("新增成功");
        this.query(1); // 新增后跳转到第一页
      } else {
        this.$message.success("编辑成功");
        this.query(this.pagination.currentPage); // 编辑后刷新当前页
      }
    }
  }
};
</script>

<style lang="less" scoped>
.exam-management {
  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 6px;

    .toolbar-left {
      display: flex;
      align-items: center;
    }

    .toolbar-right {
      .el-button {
        margin-left: 8px;
      }
    }
  }

  .title-cell {
    display: flex;
    align-items: center;

    .title-text {
      font-weight: 500;
    }
  }

  .score-text {
    font-weight: 600;
    color: #409eff;
  }

  .pagination-container {
    margin-top: 20px;
    text-align: right;
  }
}
</style>
